import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Badge,
  Paper
} from '@mui/material';
import {
  MarkAsUnread as MarkAsUnreadIcon,
  DraftsOutlined as DraftsOutlinedIcon,
  Public as PublicIcon,
  Group as GroupIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import RichTextDisplay from '../../components/common/RichTextDisplay';
import { useNotifications } from '../../contexts/NotificationContext';
import * as messageService from '../../services/messageService';

type MessageWithReadStatus = messageService.MessageWithReadStatus;

const MessagesPage: React.FC = () => {
  const [messages, setMessages] = useState<MessageWithReadStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<MessageWithReadStatus | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { decrementUnreadCount, incrementUnreadCount } = useNotifications();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await messageService.getUserMessages();
      setMessages(response.data);
      setError(null);
    } catch (err: unknown) {
      console.error('Error fetching messages:', err);
      const error = err as { message?: string };
      setError(error.message || 'Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageClick = async (message: MessageWithReadStatus) => {
    setSelectedMessage(message);
    setDialogOpen(true);

    // Mark as read if not already read
    if (!message.isRead) {
      try {
        await messageService.markMessageAsRead(message.id);
        // Update local state
        setMessages(prev => prev.map(m =>
          m.id === message.id ? { ...m, isRead: true, readAt: new Date().toISOString() } : m
        ));
        // Update global unread count
        decrementUnreadCount();
      } catch (err: unknown) {
        console.error('Error marking message as read:', err);
      }
    }
  };

  const handleToggleReadStatus = async (message: MessageWithReadStatus, event: React.MouseEvent) => {
    event.stopPropagation();

    try {
      if (message.isRead) {
        await messageService.markMessageAsUnread(message.id);
        setMessages(prev => prev.map(m =>
          m.id === message.id ? { ...m, isRead: false, readAt: undefined } : m
        ));
        // Update global unread count
        incrementUnreadCount();
      } else {
        await messageService.markMessageAsRead(message.id);
        setMessages(prev => prev.map(m =>
          m.id === message.id ? { ...m, isRead: true, readAt: new Date().toISOString() } : m
        ));
        // Update global unread count
        decrementUnreadCount();
      }
    } catch (err: unknown) {
      console.error('Error toggling read status:', err);
      const error = err as { message?: string };
      setError(error.message || 'Failed to update message status');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = messages.filter(m => !m.isRead).length;

  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h1">
            Messages
          </Typography>
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="primary">
              <Paper sx={{ px: 2, py: 0.5, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <Typography variant="body2" fontWeight="medium">
                  {unreadCount} unread
                </Typography>
              </Paper>
            </Badge>
          )}
        </Box>
        <Typography variant="body1" color="text.secondary">
          Stay updated with announcements and important information
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No messages yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You'll see announcements and important updates here when they're posted.
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {messages.map((message, index) => (
                <React.Fragment key={message.id}>
                  <ListItem
                    button
                    onClick={() => handleMessageClick(message)}
                    sx={{
                      py: 2,
                      backgroundColor: message.isRead ? 'transparent' : 'action.hover',
                      '&:hover': {
                        backgroundColor: 'action.selected'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography
                            variant="h6"
                            component="span"
                            sx={{
                              fontWeight: message.isRead ? 'normal' : 'bold',
                              color: message.isRead ? 'text.primary' : 'primary.main'
                            }}
                          >
                            {message.title}
                          </Typography>
                          <Chip
                            icon={message.messageType === 'global' ? <PublicIcon /> : <GroupIcon />}
                            label={message.messageType === 'global' ? 'General' : 'Session'}
                            size="small"
                            color={message.messageType === 'global' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                          {!message.isRead && (
                            <Chip
                              label="New"
                              size="small"
                              color="error"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Box sx={{ mb: 1 }}>
                            <RichTextDisplay 
                              content={message.content}
                              preview={true}
                              maxLength={150}
                              sx={{ 
                                fontSize: '0.875rem',
                                color: 'text.secondary',
                                '& *': { fontSize: 'inherit !important' }
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ScheduleIcon fontSize="small" />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(message.createdAt)}
                              </Typography>
                            </Box>
                            {message.messageType === 'session_specific' && message.sessionTitle && (
                              <Typography variant="caption" color="text.secondary">
                                Session: {message.sessionTitle}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              From: {message.createdByName || 'Unknown'}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleToggleReadStatus(message, e)}
                        color={message.isRead ? 'default' : 'primary'}
                        title={message.isRead ? 'Mark as unread' : 'Mark as read'}
                      >
                        {message.isRead ? <MarkAsUnreadIcon /> : <DraftsOutlinedIcon />}
                      </IconButton>
                    </Box>
                  </ListItem>
                  {index < messages.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Message Detail Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        {selectedMessage && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography variant="h5" component="div" gutterBottom>
                    {selectedMessage.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={selectedMessage.messageType === 'global' ? <PublicIcon /> : <GroupIcon />}
                      label={selectedMessage.messageType === 'global' ? 'General Announcement' : 'Session Message'}
                      size="small"
                      color={selectedMessage.messageType === 'global' ? 'primary' : 'secondary'}
                    />
                    {selectedMessage.messageType === 'session_specific' && selectedMessage.sessionTitle && (
                      <Chip
                        label={selectedMessage.sessionTitle}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Posted by {selectedMessage.createdByName || 'Unknown'} on {formatDate(selectedMessage.createdAt)}
                  </Typography>
                </Box>
                <IconButton onClick={() => setDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <RichTextDisplay content={selectedMessage.content} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </MainLayout>
  );
};

export default MessagesPage;